<template>
  <v-container fluid>
    <div class="text-h6 mt-2">
      {{ $t('title') }}
      <v-tooltip max-width="300" right>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" dark small v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
        </template>
        <span>{{ $t('tooltip') }}
          </span>
      </v-tooltip>
      <v-skeleton-loader
          v-if="dailyThrottle === null"
          class="d-inline-flex ma-2"
          height="24"
          type="button">
      </v-skeleton-loader>
      <v-tooltip v-else bottom max-width="300">
        <template v-slot:activator="{ on, attrs }">
          <v-chip
              :color="remainingChecksCount === 0 ? 'error' : (remainingChecksCount < 10 ? 'warning' : 'success')"
              class="ma-2"
              label
              outlined
              small
              to="/account/subscriptions"
              v-bind="attrs"
              v-on="on">
            {{ dailyThrottle }} / {{ $account.current.rules['check-position'].max_check_per_day }}
            {{ $t('dates.per-day') }}
          </v-chip>
        </template>
        <span>
          {{ $t('create-checks') }}
          {{ $account.current.rules['check-position'].max_check_per_day }}
          {{ $t('increase-quota') }}
        </span>
      </v-tooltip>
    </div>

    <div class="mt-5 mb-3">
      <a-action-buttons-bar
          :buttons="buttons"
          :options="{ selected: table.selected}">
      </a-action-buttons-bar>

      <v-expand-transition>
        <div v-show="add">
          <page-form ref="form" @submit="onFormSubmit"></page-form>
        </div>
      </v-expand-transition>
    </div>

    <page-table
        ref="table"
        @fetched="handleTableFetched"
        @select="handleTableSelect">
    </page-table>

    <check-serp-dialog></check-serp-dialog>
    <remove-check-dialog></remove-check-dialog>
    <restart-checks-dialog></restart-checks-dialog>
  </v-container>
</template>

<script>
import PageTable from '@/views/Pages/DatacenterCheck/Components/PageTable'
import PageForm from '@/views/Pages/DatacenterCheck/Components/PageForm'
import CheckSerpDialog from '@/views/Pages/DatacenterCheck/Dialogs/CheckSerpDialog'
import RemoveCheckDialog from '@/views/Pages/DatacenterCheck/Dialogs/RemoveCheckDialog'
import RestartChecksDialog from '@/views/Pages/DatacenterCheck/Dialogs/RestartChecksDialog'
import Alert3 from '@/plugins/common/account/Components/Shared/Alert3.vue'
import Alert7 from '../../../plugins/common/account/Components/Shared/Alert7.vue'

export default {
  name: 'DatacenterCheckPage',
  components: {Alert7, Alert3, RestartChecksDialog, RemoveCheckDialog, CheckSerpDialog, PageForm, PageTable},
  data() {
    return {
      add: false,
      table: {
        selected: []
      },
      buttons: [
        {
          color: 'success',
          text: this.$t('actions.add'),
          icon: 'mdi-plus',
          click: () => this.add = !this.add,
        },
        {
          color: 'error',
          text: ({selected}) => this.$t('actions.remove-count', {count: selected.length}),
          icon: 'mdi-delete',
          disabled: ({selected}) => selected.length === 0,
          click: ({selected}) => this.$ui.openDialog('remove_checks', {ids: selected}),
        },
        {
          color: 'warning',
          text: ({selected}) => this.$t('actions.restart-count', {count: selected.length}),
          icon: 'mdi-refresh',
          disabled: ({selected}) => selected.length === 0 || selected.length > 4 || selected.length > this.remainingChecksCount,
          click: ({selected}) => this.$ui.openDialog('restart_checks', {ids: selected}),
        }
      ],
      dailyThrottle: null,
    }
  },
  computed: {
    remainingChecksCount() {
      if (this.dailyThrottle === null) {
        return 0
      } else {
        return this.$account.current.rules['check-position'].max_check_per_day - this.dailyThrottle
      }
    }
  },
  methods: {
    handleTableSelect(ids) {
      this.table.selected = ids
    },
    handleTableFetched(data) {
      this.dailyThrottle = data.meta.daily_throttle
    },
    onFormSubmit({check}) {
      this.$refs['table'].onFormSubmit({check})
    },
  }
}
</script>


<i18n>
{
  "en": {
    "create-checks": "You can make up to",
    "increase-quota": "checks per day. Click here to increase this limit.",
    "title": "Position by data center",
    "tooltip": "This tool checks the position of your website on a keyword in several Google data centers to accurately determine your position."
  },
  "es": {
    "create-checks": "Puedes compensar hasta",
    "increase-quota": "cheques por día. Haga clic aquí para aumentar este límite.",
    "title": "Posición por centro de datos",
    "tooltip": "Esta herramienta verifica la posición de su sitio web en una palabra clave en varios centros de datos de Google para determinar con precisión su posición."
  },
  "fr": {
    "create-checks": "Vous pouvez effectuer jusqu'à ",
    "increase-quota": "vérifications par jour. Cliquez ici pour augmenter cette limite.",
    "title": "Position par datacenter",
    "tooltip": "Cet outil vérifie la position de votre site web sur un mot clé dans plusieurs datacenters Google afin de déterminer avec précision votre position."
  },
  "it": {
    "create-checks": "Puoi effettuare fino a",
    "increase-quota": "verifiche al giorno. Clicca qui per aumentare questo limite.",
    "title": "Posizione per datacenter",
    "tooltip": "Questo strumento verifica la posizione del tuo sito web su una parola chiave in diversi datacenter di Google per determinare con precisione la tua posizione."
  },
  "pt": {
    "create-checks": "Pode efetuar até",
    "increase-quota": "verificações por dia. Clique aqui para aumentar este limite.",
    "title": "Posição por datacenter",
    "tooltip": "Esta ferramenta verifica a posição do seu website numa palavra-chave em vários datacenters do Google para determinar com precisão a sua posição."
  },
  "pl": {
    "create-checks": "Możesz wykonać do",
    "increase-quota": "sprawdzeń dziennie. Kliknij tutaj, aby zwiększyć ten limit.",
    "title": "Pozycja według centrum danych",
    "tooltip": "To narzędzie sprawdza pozycję Twojej witryny dla słowa kluczowego w kilku centrach danych Google, aby dokładnie określić Twoją pozycję."
  }
}
</i18n>
